import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { AppRouter } from './router.tsx';
import { useAuthStore } from './store/auth.ts';
import { useSocketStore } from './store/socket.ts';

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false, retry: 1 } } });

export function App(): JSX.Element {
  const hydrate = useAuthStore((s) => s.hydrateMe);
  const ssoBootstrap = useAuthStore((s) => s.ssoBootstrap);
  const userId = useAuthStore((s) => s.user?.id);
  const accessToken = useAuthStore((s) => s.accessToken);

  // Ambient-SSO cold-boot gate: if there's no local session, try to resolve the
  // parent-domain wap_sso cookie into a session BEFORE rendering (so a
  // just-logged-in-elsewhere user lands signed in, not on a login flash). If
  // there's already a session, render immediately. Never blocks longer than the
  // bootstrap call (which 204s fast when Office is down / no cookie).
  const [ready, setReady] = useState(() => Boolean(useAuthStore.getState().accessToken));

  useEffect(() => {
    if (ready) { void hydrate(); return; }
    let cancelled = false;
    void ssoBootstrap().finally(() => {
      if (cancelled) return;
      setReady(true);
      void hydrate();
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ambient SSO can swap the authenticated user IN PLACE: ssoBootstrap(true),
  // called from the 401 interceptor, replaces the session with no reload and no
  // cache clear. On a shared council-office machine the React Query cache would
  // otherwise still hold the previous user's data — including their strictly
  // private meeting notes. Drop every cached query whenever the identity
  // changes. (Logout hard-navigates, which drops the cache on its own.)
  const prevUserId = useRef(userId);
  useEffect(() => {
    if (prevUserId.current === userId) return;
    prevUserId.current = userId;
    qc.clear();
  }, [userId]);

  // Connect the socket when authenticated; reconnect when the access token
  // is refreshed (so the socket auth stays fresh); disconnect on logout/unmount.
  // socket.ts connect() guards `if (get().socket) return`, so we disconnect
  // first to let a token-refresh reconnect with the new credential.
  // No circular import: socket.ts already imports from auth.ts (not vice versa),
  // and here we import socket.ts into App.tsx which is the top-level entry.
  useEffect(() => {
    if (userId && accessToken) {
      useSocketStore.getState().connect();
      return () => useSocketStore.getState().disconnect();
    }
    return undefined;
  }, [userId, accessToken]);

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
        <span
          aria-hidden
          className="size-6 animate-spin rounded-full border-2 border-current border-t-transparent text-muted-foreground"
        />
      </div>
    );
  }

  return (
    <QueryClientProvider client={qc}>
      <AppRouter />
    </QueryClientProvider>
  );
}
